import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useAuthStore from "../../store/authStore";
import api from "../../api/client";
import Logo from "../../components/Logo";
import ThemeToggle from "../../components/ThemeToggle";
import LoadingSpinner from "../../components/LoadingSpinner";
import PushStatusPanel from "../../components/PushStatusPanel";
import {
  Calendar,
  FileText,
  FlaskConical,
  MessageCircle,
  ClipboardList,
  Users,
  Clock,
  CheckCircle,
  XCircle,
  Send,
} from "lucide-react";
import EmployerNavBar from "../../components/EmployerNavBar";
import { statusClasses, ROLE_CLASSES } from "../../constants/statusColors";

const ACTION_ICONS = {
  meeting: Calendar,
  document_request: FileText,
  assessment: FlaskConical,
  info: MessageCircle,
};

const DEFAULT_STATUSES = [
  "Applied",
  "Screening",
  "Interview",
  "Technical test",
  "Final interview",
  "Offer received",
  "Accepted",
  "Rejected",
  "Withdrawn",
];

// ── Process History Panel ─────────────────────────────────────

function ProcessHistoryPanel({ candidate, employerToken, onClose }) {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);

  const authConfig = {
    headers: { Authorization: `Bearer ${employerToken}` },
    withCredentials: true,
  };

  useEffect(() => {
    api
      .get(
        `/employer/connected-candidates/${candidate.application_id}/history`,
        authConfig,
      )
      .then(({ data }) => setHistory(data))
      .catch(() => setHistory({ statuses: [] }))
      .finally(() => setLoading(false));
  }, [candidate.application_id]);

  const formatDate = (iso) => {
    if (!iso) return null;
    return new Date(iso).toLocaleString("en-SE", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
      <div className="fixed top-0 right-0 h-full w-full max-w-lg bg-white dark:bg-brand-dark-surface shadow-2xl z-50 flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-white/10 flex-shrink-0">
          <div>
            <h2 className="font-semibold text-gray-900 dark:text-white">
              Process history
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {candidate.job_title} · {candidate.company}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-1"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {loading && (
            <div className="flex flex-col gap-4 animate-pulse">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-16 bg-gray-100 dark:bg-white/5 rounded-xl"
                />
              ))}
            </div>
          )}
          {!loading && history?.statuses.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-12">
              No status history yet.
            </p>
          )}
          {!loading && history?.statuses.length > 0 && (
            <div className="flex flex-col gap-6">
              {history.statuses.map((s, i) => (
                <div key={s.id} className="flex gap-3">
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div
                      className={`w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1 ${i === 0 ? "bg-brand-violet" : "bg-gray-300 dark:bg-gray-600"}`}
                    />
                    {i < history.statuses.length - 1 && (
                      <div className="w-px flex-1 min-h-8 bg-gray-200 dark:bg-white/10 mt-1" />
                    )}
                  </div>
                  <div className="flex-1 pb-4">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span
                        className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusClasses(s.status)}`}
                      >
                        {s.status}
                      </span>
                      <span
                        className={`text-xs font-medium ${s.source === "employer" ? "text-brand-violet dark:text-brand-violet-on-dark" : "text-gray-400"}`}
                      >
                        {s.source === "employer" ? "via you" : "by candidate"}
                      </span>
                    </div>
                    {s.note && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                        {s.note}
                      </p>
                    )}
                    <p className="text-xs text-gray-400">
                      {formatDate(s.created_at)}
                    </p>
                    {s.actions.length > 0 && (
                      <div className="flex flex-col gap-2 mt-3">
                        {s.actions.map((a) => {
                          const IconComponent =
                            ACTION_ICONS[a.action_type] ?? ClipboardList;
                          return (
                            <div
                              key={a.id}
                              className="bg-gray-50 dark:bg-brand-dark-bg rounded-xl p-3 border border-gray-100 dark:border-white/10"
                            >
                              <div className="flex items-start gap-2">
                                <IconComponent className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-medium text-gray-900 dark:text-white">
                                    {a.title}
                                  </p>
                                  {a.description && (
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                      {a.description}
                                    </p>
                                  )}
                                  {a.meeting_at && (
                                    <p className="text-xs text-brand-violet dark:text-brand-violet-on-dark mt-0.5 flex items-center gap-1">
                                      <Calendar className="w-3 h-3" />
                                      {formatDate(a.meeting_at)}
                                      {a.duration_minutes &&
                                        ` · ${a.duration_minutes} min`}
                                    </p>
                                  )}
                                  {a.attendees?.length > 0 && (
                                    <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
                                      <Users className="w-3 h-3" />
                                      {a.attendees.join(", ")}
                                    </p>
                                  )}
                                  {a.deadline && (
                                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5 flex items-center gap-1">
                                      <Clock className="w-3 h-3" />
                                      Deadline: {formatDate(a.deadline)}
                                    </p>
                                  )}
                                  {a.response && (
                                    <span
                                      className={`inline-flex items-center gap-1 text-xs font-medium mt-1 px-2 py-0.5 rounded-full ${
                                        a.response === "accepted" ||
                                        a.response === "submitted"
                                          ? "bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400"
                                          : "bg-red-100 text-red-600 dark:bg-red-900/20 dark:text-red-400"
                                      }`}
                                    >
                                      {a.response === "accepted" ||
                                      a.response === "submitted" ? (
                                        <CheckCircle className="w-3 h-3" />
                                      ) : (
                                        <XCircle className="w-3 h-3" />
                                      )}
                                      {a.response}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ── Main Component ────────────────────────────────────────────

export default function AgencyCandidates() {
  const navigate = useNavigate();
  const { employerToken, employerUser, clearEmployerAuth } = useAuthStore();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pushCandidate, setPushCandidate] = useState(null);
  const [historyCandidate, setHistoryCandidate] = useState(null);
  const [pushSuccess, setPushSuccess] = useState(null);

  const authConfig = {
    headers: { Authorization: `Bearer ${employerToken}` },
    withCredentials: true,
  };

  useEffect(() => {
    document.title = "Joborigo — My Candidates";
    fetchCandidates();
  }, []);

  useEffect(() => {
    if (employerUser && !employerUser.is_agency) {
      navigate("/employers/dashboard");
    }
  }, [employerUser]);

  const fetchCandidates = async () => {
    try {
      const { data } = await api.get(
        "/employer/connected-candidates",
        authConfig,
      );
      setCandidates(data);
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await api.post("/employer/auth/logout", {}, authConfig);
    } catch {}
    clearEmployerAuth();
    navigate("/employers/login");
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-brand-dark-bg text-gray-900 dark:text-gray-100">
      <EmployerNavBar />

      <main className="max-w-5xl mx-auto px-6 py-10">
        <div className="mb-8">
          <h1 className="text-2xl font-bold">My Candidates</h1>
          <p className="text-gray-400 text-sm mt-1">
            All candidates you are currently managing — push updates, view
            process history, track responses.
          </p>
        </div>

        {pushSuccess && (
          <div className="mb-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl px-4 py-3 flex items-center justify-between">
            <p className="text-green-700 dark:text-green-400 text-sm font-medium">
              Status update pushed successfully to {pushSuccess}.
            </p>
            <button
              onClick={() => setPushSuccess(null)}
              className="text-green-600 text-xs hover:underline"
            >
              Dismiss
            </button>
          </div>
        )}

        {loading ? (
          <div className="text-center py-20">
            <LoadingSpinner />
          </div>
        ) : candidates.length === 0 ? (
          <div className="bg-white dark:bg-brand-dark-surface rounded-2xl border border-dashed border-gray-200 dark:border-white/10 p-16 text-center">
            <p className="text-gray-400 text-sm font-medium mb-1">
              No candidates yet
            </p>
            <p className="text-gray-400 text-xs mb-4">
              Connect a candidate using their connect token from your dashboard,
              or find candidates in the Arena.
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={() => navigate("/employers/dashboard")}
                className="text-xs text-brand-violet dark:text-brand-violet-on-dark hover:underline"
              >
                Go to dashboard →
              </button>
              <button
                onClick={() => navigate("/employers/arena")}
                className="text-xs text-brand-violet dark:text-brand-violet-on-dark hover:underline"
              >
                Browse Arena →
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {candidates.map((c) => (
              <div
                key={c.integration_id}
                className="bg-white dark:bg-brand-dark-surface rounded-2xl border border-gray-100 dark:border-white/10 p-5"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                        {c.job_title}
                      </p>
                      <span className="text-gray-400 text-xs">·</span>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {c.company}
                      </p>
                      {c.candidate_ref && (
                        <span className="text-xs text-gray-400 font-mono bg-gray-50 dark:bg-brand-dark-bg px-2 py-0.5 rounded">
                          ref: {c.candidate_ref}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span
                        className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusClasses(c.current_status)}`}
                      >
                        {c.current_status}
                      </span>
                      {c.last_push_at ? (
                        <span className="text-xs text-gray-400">
                          Last update:{" "}
                          {new Date(c.last_push_at).toLocaleDateString("en-SE")}{" "}
                          · {c.last_push_status}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400 italic">
                          No updates pushed yet
                        </span>
                      )}
                      <span className="text-xs text-gray-400">
                        {c.push_count} push{c.push_count !== 1 ? "es" : ""}
                      </span>
                      <span className="text-xs text-gray-400">
                        Connected{" "}
                        {new Date(c.connected_at).toLocaleDateString("en-SE")}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => setHistoryCandidate(c)}
                      className="text-xs border border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400 px-3 py-1.5 rounded-lg hover:border-brand-violet dark:hover:border-brand-violet-on-dark hover:text-brand-violet dark:hover:text-brand-violet-on-dark transition"
                    >
                      View process
                    </button>
                    <button
                      onClick={() => setPushCandidate(c)}
                      className="flex items-center gap-1.5 text-xs bg-brand-violet text-white px-3 py-1.5 rounded-lg hover:opacity-90 transition"
                    >
                      <Send className="w-3 h-3" />
                      Push update
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {pushCandidate && (
        <PushStatusPanel
          applicationId={pushCandidate.application_id}
          subtitle={`${pushCandidate.job_title} · ${pushCandidate.company}`}
          employerToken={employerToken}
          onClose={() => setPushCandidate(null)}
          onPushed={() => {
            setPushSuccess(pushCandidate.job_title);
            fetchCandidates();
          }}
        />
      )}

      {historyCandidate && (
        <ProcessHistoryPanel
          candidate={historyCandidate}
          employerToken={employerToken}
          onClose={() => setHistoryCandidate(null)}
        />
      )}
    </div>
  );
}
